import React from "react";
import { useEffect, useState, useMemo } from "react";
import { Form, Button } from "react-bootstrap";
import activationStateBadgeClass from "./activationStateBadgeClass";
import CustomTable from "../CustomTable";
import FormModal from "../FormModal";
import Modal from "../Modal";
import RecordCount from "../utils/RecordCount";
import Loading from "../utils/Loading";
import JiraTicket from "../JiraTicket";
import RoleSelect from "../RoleSelect";
import GrantAccessModal from "../UserEntities/GrantAccessModal";
import SupportModal from "./SupportModal";

const Users = (props) => {
  const [data, setData] = useState(props.users);
  const [isLoading, setIsLoading] = useState(true);
  const [showModalUpdateUser, setShowModalUpdateUser] = useState(false);
  const [modalUpdateUserId, setModalUpdateUserId] = useState(null);
  const [userToUpdate, setUserToUpdate] = useState(null);
  const [entitiesTitle, setEntitiesTitle] = useState(null);
  const [userEntities, setUserEntities] = useState([]);
  const [showEntitiesModal, setShowEntitiesModal] = useState(null);
  const [showSupportModal, setShowSupportModal] = useState(null);
  const [showDisassociateModal, setShowDisassociateModal] = useState(false);
  const [showGrantAccessModal, setShowGrantAccessModal] = useState(false);
  const [showModalRecover, setShowModalRecover] = useState(false);
  const [modalRecoverId, setModalRecoverId] = useState(null);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [modalDeleteId, setModalDeleteId] = useState(null);
  const [showModalUpdateUserEntity, setShowModalUpdateUserEntity] = useState(false);

  const isEntityContext = props.id_340b;

  useEffect(() => {
    if (!data && props.users_path) {
      const fetchData = async () => {
        try {
          const response = await fetch(props.users_path, {
            headers: { Accept: "application/json" },
          });
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          const fetchedData = await response.json();
          setData(fetchedData);
        } catch (error) {
          console.error("Error fetching data: ", error);
        } finally {
          setIsLoading(false);
        }
      };
      if (!data || data.length === 0) {
        fetchData();
      } else {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  const UpdateUserFormFields = (obj) => {
    const disabledBasicFields = !obj.canUpdateBasicFields;

    return (
      <React.Fragment>
        <Form.Group className="mb-3" controlId="first_name">
          <Form.Label>First Name</Form.Label>
          <Form.Control
            required
            disabled={disabledBasicFields}
            type="text"
            name="user[first_name]"
            placeholder="Enter first name..."
            defaultValue={obj.user.first_name}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="last_name">
          <Form.Label>Last Name</Form.Label>
          <Form.Control
            required
            disabled={disabledBasicFields}
            type="text"
            name="user[last_name]"
            placeholder="Enter last name..."
            defaultValue={obj.user.last_name}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="email">
          <Form.Label>Email</Form.Label>
          <Form.Control
            required
            disabled={disabledBasicFields}
            type="text"
            name="user[email]"
            placeholder="Enter email..."
            defaultValue={obj.user.email}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="phone">
          <Form.Label>Phone</Form.Label>
          <Form.Control
            disabled={disabledBasicFields}
            type="text"
            name="user[phone]"
            placeholder="Enter phone..."
            defaultValue={obj.user.phone}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="prospect">
          <Form.Label>Prospect</Form.Label>
          <Form.Control
            readOnly
            required
            disabled={disabledBasicFields}
            type="text"
            name="user[prospect]"
            defaultValue={obj.user.prospect}
          />
        </Form.Group>
      </React.Fragment>
    );
  };

  const UpdateUserEntityFormFields = (obj) => {
    const disabledBasicFields = !obj.canUpdateBasicFields;

    const entityRole = obj.user.entities.find((e) => e.id_340b === props.id_340b)?.role;
    const entityRoleOptions = [
      { label: "User", value: "user" },
      { label: "Admin", value: "admin" },
    ].filter((option) => option.value !== entityRole);

    return (
      <React.Fragment>
        <input type="hidden" name="_method" value="PATCH" />
        <Form.Control type="hidden" name="user[user_entity][id_340b]" value={props.id_340b} />
        <Form.Group className="mb-3" controlId="entity_role">
          <Form.Label>Entity Role</Form.Label>
          <RoleSelect
            disabled={disabledBasicFields || !props.canUpdateUserEntity}
            currentRole={entityRole}
            roleOptions={entityRoleOptions}
            name={"role"}
          />
        </Form.Group>
      </React.Fragment>
    );
  };

  const getGrantAccessFormFields = (userName) => {
    return (
      <React.Fragment>
        <Form.Label>
          Are you sure you want to grant {userName} access to {props.id_340b}? Please confirm they
          have provided the necessary verification. If not, please click "Reject".
        </Form.Label>
      </React.Fragment>
    );
  };

  function canGrantEntityAccess(tableProps) {
    return (
      props.canGrantEntityAccess &&
      tableProps.row.original.user_entities[props.id_340b] === "pending"
    );
  }

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "full_name",
        filter: "fuzzyText",
        Cell: (tableProps) => (
          <div className="text-nowrap">
            {tableProps.row.original.deleted_at ? (
              <>
                <i className="fa fa-close me-2 text-danger" title="Deleted User"></i>{" "}
                <i className="text-danger">{tableProps.value}</i>
                <div className="text-danger fst-italic">
                  <small>Deleted {tableProps.row.original.deleted_at}</small>
                </div>
              </>
            ) : (
              <>
                <a href={`/user/${tableProps.row.original.id}`} target="_blank">
                  <i className="fa fa-user me-2"></i>
                </a>
                {tableProps.row.original.full_name}
              </>
            )}
          </div>
        ),
      },
      {
        Header: "Email",
        accessor: "email",
        filter: "fuzzyText",
        Cell: (row) => (
          <div>
            <small className="text-muted">{row.value}</small>
          </div>
        ),
      },
      ...(isEntityContext
        ? [
            {
              Header: "User-Entity Status",
              accessor: "user_entity_status",
              filter: "fuzzyText",
              Cell: (tableProps) => (
                <div
                  className={
                    "badge text-uppercase fw-normal bg-" +
                    activationStateBadgeClass(tableProps.row.original.user_entities[props.id_340b])
                  }
                >
                  {tableProps.row.original.user_entities[props.id_340b]}
                </div>
              ),
            },
            {
              Header: "Entity Role",
              accessor: "entity_role",
              filter: "fuzzyText",
              Cell: (tableProps) => (
                <div className="badge bg-light text-dark text-uppercase fw-normal">
                  {tableProps.row.original.entities.find((e) => e.id_340b === props.id_340b)?.role}
                </div>
              ),
            },
          ]
        : [
            {
              Header: "Activation State",
              accessor: "activation_state",
              filter: "fuzzyText",
              Cell: (tableProps) => (
                <div
                  className={
                    "badge text-uppercase fw-normal bg-" +
                    activationStateBadgeClass(tableProps.row.original.activation_state)
                  }
                >
                  {tableProps.row.original.activation_state}
                </div>
              ),
            },
          ]),
      {
        Header: "340B CE's IDs",
        accessor: "entity_ids",
        filter: "fuzzyText",
        Cell: (tableProps) =>
          tableProps.row.original.entities.length > 0 ? (
            <small>
              <a href={`/entities/340bid/${tableProps.row.original.entities[0].id_340b}?tab=users`}>
                {tableProps.row.original.entities[0].id_340b}
              </a>
              <a
                href="#"
                onClick={() => viewEntities(tableProps.row.original)}
                className="badge bg-primary text-light ms-2 fw-normal"
              >
                {tableProps.row.original.entities.length > 1
                  ? `+${tableProps.row.original.entities.length - 1} more`
                  : "See details"}
              </a>
            </small>
          ) : null,
      },
      {
        Header: "Prospect",
        accessor: "prospect",
        filter: "fuzzyText",
        Cell: (row) => (
          <div
            className={`badge ${
              !row.value ? "bg-light text-dark" : "bg-danger text-light"
            } fw-normal text-uppercase`}
          >
            {row.value ? "True" : "False"}
          </div>
        ),
      },
      {
        Header: "Locked",
        accessor: "locked",
        filter: "fuzzyText",
        Cell: (row) => (
          <div
            className={`badge ${
              !row.value ? "bg-light text-dark" : "bg-danger text-light"
            } fw-normal text-uppercase`}
          >
            {row.value ? "True" : "False"}
          </div>
        ),
      },
      {
        Header: "Actions",
        Cell: (tableProps) => (
          <div className="text-nowrap">
            {tableProps.row.original.deleted_at || tableProps.row.original.organization_deleted ? (
              <React.Fragment>
                <Button
                  className="btn-sm btn-danger"
                  onClick={() => {
                    setShowModalRecover(true);
                    setModalRecoverId(tableProps.row.original.id);
                  }}
                >
                  Recover
                </Button>
              </React.Fragment>
            ) : (
              <>
                <a
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    setModalUpdateUserId(tableProps.row.original.id);
                    setUserToUpdate(tableProps.row.original);
                    isEntityContext
                      ? setShowModalUpdateUserEntity(true)
                      : setShowModalUpdateUser(true);
                  }}
                >
                  <i className="fa-solid fa-pen"></i>
                </a>
                <a
                  className="btn btn-success btn-sm ms-2"
                  onClick={() => {
                    setUserToUpdate(tableProps.row.original);
                    setShowSupportModal(true);
                  }}
                >
                  <i className="fa fa-stethoscope"></i>
                </a>
                {isEntityContext ? (
                  <>
                    {canGrantEntityAccess(tableProps) ? (
                      <a
                        className="btn btn-success btn-sm ms-2"
                        onClick={() => {
                          setModalUpdateUserId(tableProps.row.original.id);
                          setShowGrantAccessModal(true);
                        }}
                      >
                        <i className="fa fa-user-check"></i>
                      </a>
                    ) : null}
                    {props.canUpdateUserEntity ? (
                      <a
                        className="btn btn-danger btn-sm ms-2"
                        onClick={() => {
                          setModalDeleteId(tableProps.row.original.id);
                          setShowDisassociateModal(true);
                        }}
                      >
                        <i className="fa fa-heart-crack"></i>
                      </a>
                    ) : null}
                  </>
                ) : (
                  <a
                    className="btn btn-danger btn-sm ms-2"
                    onClick={() => {
                      setShowModalDelete(true);
                      setModalDeleteId(tableProps.row.original.id);
                    }}
                  >
                    <i className="fa fa-trash"></i>
                  </a>
                )}
              </>
            )}
          </div>
        ),
      },
    ],
    []
  );

  const entity_columns = useMemo(() => [
    {
      Header: "340B ID",
      accessor: "id_340b",
      filter: "fuzzyText",
      Cell: (tableProps) => (
        <a
          className="badge bg-light text-dark fw-normal text-uppercase"
          href={`/entities/340bid/${tableProps.row.original.id_340b}`}
        >
          {tableProps.row.original.id_340b}
        </a>
      ),
    },
    {
      Header: "Name",
      accessor: "name",
      filter: "fuzzyText",
      Cell: (tableProps) => (
        <a
          href={`/entities/340bid/${tableProps.row.original.id_340b}`}
          className="text-dark fw-bold"
        >
          {tableProps.row.original.name}
        </a>
      ),
    },
    {
      Header: "Role",
      accessor: "role",
      filter: "fuzzyText",
      Cell: (tableProps) => (
        <div className="badge bg-light text-dark text-uppercase fw-normal">
          {tableProps.row.original.role}
        </div>
      ),
    },
    {
      Header: "Action",
      filter: "fuzzyText",
      Cell: (tableProps) => (
        <a
          className="btn btn-primary btn-sm"
          href={`/entities/340bid/${tableProps.row.original.id_340b}`}
        >
          View
        </a>
      ),
    },
  ]);

  function viewEntities(row) {
    setEntitiesTitle(`${row.full_name} Entities`);
    setUserEntities(row.entities);
    setShowEntitiesModal(true);
  }

  function EntitiesModal(entities) {
    return (
      <ul className="list-group">
        {entities.constructor == Array && entities.length !== 0 ? (
          <CustomTable data={entities} columns={entity_columns} />
        ) : (
          <i className="text-muted">
            <br />
            This user has no entities.
          </i>
        )}
      </ul>
    );
  }

  return (
    <React.Fragment>
      {isLoading ? (
        <Loading />
      ) : data ? (
        <>
          {!props.hide_count ? <RecordCount record_count={data.length} record_type="user" /> : null}
          {data.length > 0 || props.hide_count ? (
            <CustomTable columns={columns} data={data} />
          ) : null}
          <FormModal
            show={showModalUpdateUser}
            title={`Edit User - ${data.find((row) => row.id === modalUpdateUserId)?.email}, ${modalUpdateUserId}`}
            buttonText="Update User"
            modalId={`edit_user_modal_${modalUpdateUserId}`}
            buttonType="primary"
            fields={
              <UpdateUserFormFields
                user={userToUpdate}
                canUpdateBasicFields={props.canUpdateBasicFields}
              />
            }
            action={`/users/${modalUpdateUserId}`}
            dismiss={() => {
              setShowModalUpdateUser(false);
            }}
          />
          <FormModal
            show={showModalUpdateUserEntity}
            title={`Edit User's Entity Access - ${data.find((row) => row.id === modalUpdateUserId)?.email}`}
            buttonText="Update User Entity Role"
            modalId={`edit_user_entity_modal_${modalUpdateUserId}`}
            buttonType="primary"
            fields={
              <UpdateUserEntityFormFields
                user={userToUpdate}
                canUpdateBasicFields={props.canUpdateBasicFields}
              />
            }
            method="POST"
            action={`/entities/${props.id_340b}/users/${modalUpdateUserId}`}
            dismiss={() => {
              setShowModalUpdateUserEntity(false);
            }}
          />
          {showEntitiesModal ? (
            <Modal
              show={showEntitiesModal}
              title={entitiesTitle}
              modalId="entities_modal"
              body={EntitiesModal(userEntities)}
              buttonText="Ok"
              modalSize="large"
              buttonType="primary"
              dismiss={() => {
                setShowEntitiesModal(false);
              }}
            />
          ) : null}
          {isEntityContext ? (
            <>
              <GrantAccessModal
                show={showGrantAccessModal}
                setShow={setShowGrantAccessModal}
                fullName={data.find((row) => row.id === modalUpdateUserId)?.full_name}
                modalUserId={modalUpdateUserId}
                modalId340b={props.id_340b}
              />
              <FormModal
                show={showDisassociateModal}
                title="Remove User Access to Entity"
                buttonText="Remove"
                buttonType="danger"
                modalId={`remove_access_${modalDeleteId}`}
                fields={
                  <React.Fragment>
                    <Form.Label>
                      Are you sure you want to remove{" "}
                      {data.find((row) => row.id === modalDeleteId)?.full_name}'s access from 340B
                      ID {props.id_340b}?
                    </Form.Label>
                    <JiraTicket />
                  </React.Fragment>
                }
                method="DELETE"
                action={`/entities/${props.id_340b}/users/${modalDeleteId}`}
                dismiss={() => {
                  setShowDisassociateModal(false);
                }}
              />
            </>
          ) : null}
          <SupportModal
            user={userToUpdate}
            showModal={showSupportModal}
            setShowModal={setShowSupportModal}
            espDomain={props.envESPAppDomain}
          />
          <FormModal
            show={showModalDelete}
            title="Delete User"
            buttonText="Delete"
            buttonType="danger"
            modalId={`delete_blocked_${modalDeleteId}`}
            fields={`Are you sure you want to delete ${
              data.find((row) => row.id === modalDeleteId)?.email
            }`}
            action={`/user/${modalDeleteId}/destroy`}
            dismiss={() => {
              setShowModalDelete(false);
            }}
          />
          <FormModal
            show={showModalRecover}
            title={`Recover User - ${data.find((row) => row.id === modalRecoverId)?.email}, ${modalRecoverId}`}
            buttonText="Recover"
            modalId={`recover_user_modal_${modalRecoverId}`}
            buttonType="primary"
            fields={`Are you sure you want to recover ${
              data.find((row) => row.id === modalRecoverId)?.full_name
            }'s account?`}
            method="PATCH"
            action={`/users/${modalRecoverId}/recover`}
            dismiss={() => {
              setShowModalRecover(false);
            }}
          />
        </>
      ) : null}
    </React.Fragment>
  );
};

export default Users;
